import axios from 'axios'

import { getMIMETypeFromFileName } from './string'

/**
 * Fetches file content from a signed URL and triggers a local download via blob.
 * Cross-origin signed URLs ignore the anchor `download` attribute, so the file must
 * be fetched first and saved from an object URL.
 */
export const downloadFromSignedUrl = async (signedUrl: string, fileName: string): Promise<void> => {
  const { data } = await axios.get<ArrayBuffer>(signedUrl, { responseType: 'arraybuffer' })
  const mimeType = getMIMETypeFromFileName(fileName)
  downloadBlob(data, fileName, mimeType)
}

/**
 * Downloads a file using a temporary hidden <a> and an url.
 *
 * - If url is a normal(signed or not) url then the browser will attempt to download it directly.
 *    Some browsers may still open certain file types (like HTML) in a new tab based on their settings.
 *
 * - If url is a DataURL or ObjectURL then it downloads and saves it directly with the provided filename.
 *
 * @param url string
 * @param downloadedFileName string
 * @param target string - '_blank' or '_self' to open the link in a new tab or the same tab respectively
 */
export const download = (url: string, downloadedFileName: string, target: '_blank' | '_self' = '_self') => {
  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.setAttribute('download', downloadedFileName)
  a.setAttribute('target', target)
  a.setAttribute('style', 'visibility:hidden')
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/**
 * Triggers a browser file download by creating a temporary anchor element.
 * @param data - File content as string or ArrayBuffer
 * @param fileName - Name for the downloaded file (e.g. 'report.csv')
 * @param fileType - MIME type (e.g. 'text/csv', 'application/pdf', 'application/json')
 */
export const downloadBlob = (data: string | ArrayBuffer, fileName: string, fileType: string): void => {
  const blob = new Blob([data], { type: fileType })
  const objectUrl = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.download = fileName
  a.href = objectUrl
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(objectUrl)
}

/**
 * Generic function to download base64 encoded file (with or without mimetype prefix).
 * It decodes the base64, it creates a blob from it and downloads it using an Object URL and a hidden link.
 * https://stackoverflow.com/a/58158656
 * https://stackoverflow.com/a/16245768
 *
 *  TODO: this needs to be tested for all downloadable file formats that app supports
 *   and for the maximum expected file size
 *   and for each major target browser
 */
export const downloadBase64 = (base64EncodedFile: string, downloadedFileName: string, contentType?: string) => {
  if (!base64EncodedFile?.length) return

  // Check if mimetype prefix already embedded in the encoded data. This has priority over any contentType passed by caller.
  const mimeTypeFromPrefix = getMimeTypeFromBase64Prefix(base64EncodedFile)

  let blob: Blob

  if (mimeTypeFromPrefix) {
    // base64 with mimetype prefix found. Strip prefix from base64 data before decoding.
    blob = base64toBlob(base64EncodedFile.split(';base64,')[1], mimeTypeFromPrefix)
  } else {
    // prefix not found
    const mimeType = contentType || 'application/octet-stream'
    blob = base64toBlob(base64EncodedFile, mimeType)
  }
  const objectUrl = window.URL.createObjectURL(blob)
  download(objectUrl, downloadedFileName)
  window.URL.revokeObjectURL(objectUrl)
}

/**
 * Converts a base64 string to a Blob object.
 *
 * This function processes the base64 data in chunks to handle large files efficiently
 * and avoid potential memory issues with very large strings.
 *
 * @param b64Data - The base64 encoded data (without the data:type;base64, prefix)
 * @param mimeType - The MIME type for the blob (e.g., 'application/pdf', 'image/png')
 * @param sliceSize - Size of chunks to process the data in (default: 512 bytes)
 * @returns A Blob object containing the decoded data
 */
const base64toBlob = (b64Data: string, mimeType: string | undefined, sliceSize = 512): Blob => {
  const byteCharacters = window.atob(b64Data)
  const byteArrays = []
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)
    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, mimeType ? { type: mimeType } : {})
}

/**
 * Returns the mimetype part (e.g. application/pdf) from a base64 encoded data (or undefined if prefix does not exist).
 * Base64 with mimetype prefix has specific structure as:
 *  data:<mimetype>;base64,<actualData>
 */
const getMimeTypeFromBase64Prefix = (base64Data: string): string | undefined => {
  if (!base64Data.startsWith('data:')) {
    return undefined
  }
  return base64Data.substring(base64Data.indexOf(':') + 1, base64Data.indexOf(';'))
}
